// Storage helper: reads/writes workstation id + display label for a sales channel.
// Two independent channels: "retail" (POS lẻ) and "wholesale" (B2B sỉ).
//
// Keys kept backward-compatible:
//   retail   → id:   pos_workstation_id
//              label: pos_workstation_label  (JSON {workstationName, warehouseName})
//   wholesale → id:   wholesale_workstation_id
//              label: wholesale_workstation_label
//
// The label key is written only when an admin selects from the dropdown so that
// cashier machines can display warehouseName without hitting the admin-only endpoint.

import { useState, useCallback } from "react";

export type WorkstationChannel = "retail" | "wholesale";

interface WorkstationLabel {
  workstationName: string;
  warehouseName: string;
}

interface WorkstationConfig {
  workstationId: string | null;
  workstationName: string | null;
  warehouseName: string | null;
  isConfigured: boolean;
  setWorkstation: (ws: { id: string; name: string; warehouseName: string }) => void;
  clear: () => void;
}

function keysForChannel(channel: WorkstationChannel) {
  if (channel === "retail") {
    return {
      idKey: "pos_workstation_id",
      labelKey: "pos_workstation_label",
    };
  }
  return {
    idKey: "wholesale_workstation_id",
    labelKey: "wholesale_workstation_label",
  };
}

function readLabel(labelKey: string): WorkstationLabel | null {
  try {
    const raw = localStorage.getItem(labelKey);
    if (!raw) return null;
    return JSON.parse(raw) as WorkstationLabel;
  } catch {
    return null;
  }
}

export function useWorkstationConfig(channel: WorkstationChannel): WorkstationConfig {
  const { idKey, labelKey } = keysForChannel(channel);

  const [workstationId, setWorkstationIdState] = useState<string | null>(() => {
    return localStorage.getItem(idKey) || null;
  });

  const [label, setLabelState] = useState<WorkstationLabel | null>(() => {
    return readLabel(labelKey);
  });

  const setWorkstation = useCallback(
    (ws: { id: string; name: string; warehouseName: string }) => {
      localStorage.setItem(idKey, ws.id);
      const newLabel: WorkstationLabel = {
        workstationName: ws.name,
        warehouseName: ws.warehouseName,
      };
      localStorage.setItem(labelKey, JSON.stringify(newLabel));
      setWorkstationIdState(ws.id);
      setLabelState(newLabel);
    },
    [idKey, labelKey],
  );

  const clear = useCallback(() => {
    localStorage.removeItem(idKey);
    localStorage.removeItem(labelKey);
    setWorkstationIdState(null);
    setLabelState(null);
  }, [idKey, labelKey]);

  return {
    workstationId,
    workstationName: label?.workstationName ?? null,
    warehouseName: label?.warehouseName ?? null,
    isConfigured: !!workstationId,
    setWorkstation,
    clear,
  };
}
